import { useLocation, useParams, useSubmit } from 'react-router';
import { Button } from '~/common/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/common/components/ui/dropdown-menu';
import { LucideChevronDown, LucideGlobe, LucideTriangleAlert } from 'lucide-react';
import { AREA_LAYOUT_ACTION_INTENT } from '~/shell/routes/AreaLayout';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { cn } from '~/common/utils/styleUtil';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '~/common/components/ui/dialog';
import { Media } from '~/media.ts';
import { useBrowserTranslationActive } from '~/common/hooks/useBrowserTranslationActive';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/common/components/ui/tooltip';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';

function getLanguageNavigationLabel(langOption: string) {
  if (langOption === 'it') {
    return 'Italiano';
  }
  if (langOption === 'fr') {
    return 'Fran\u00e7ais';
  }
  return 'Deutsch';
}

/**
 * Language switcher component. Submits a form to change the language. The action is handled by the root route.
 */
export function LanguageSwitcher({
  redirectUrl,
  className,
  onClose,
  alternativeLanguage,
  getRedirectUrl
}: Readonly<{
  redirectUrl?: string;
  className?: string;
  onClose?: () => void;
  alternativeLanguage?: string;
  getRedirectUrl?: (lang: string) => string;
}>) {
  const params = useParams();
  const submit = useSubmit();
  const location = useLocation();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isBrowserTranslating = useBrowserTranslationActive();

  const lang = useMemo(() => {
    return params.lang ?? alternativeLanguage;
  }, [alternativeLanguage, params.lang]);

  const handleLanguageChange = useCallback(
    (langOption: string) => {
      let redirectTo = getRedirectUrl ? getRedirectUrl(langOption) : redirectUrl;
      if (!redirectTo) {
        const currentPath = location.pathname.replace(/^\/(de|fr|it)/, '');
        redirectTo = `/${langOption}${currentPath}${location.search}`;
      }

      const formData = new FormData();
      formData.append(FORM_FIELDS.Intent, AREA_LAYOUT_ACTION_INTENT.ChangeLanguage);
      formData.append(FORM_FIELDS.Language, langOption);
      formData.append(FORM_FIELDS.RedirectTo, redirectTo);

      setIsOpen(false);

      if (onClose) {
        onClose();
      }

      submit(formData, { method: 'post', action: `/${lang}/` }); // Action will be handled by language route
    },
    [getRedirectUrl, lang, location.pathname, location.search, onClose, redirectUrl, submit]
  );

  const browserTranslationWarning = isBrowserTranslating ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={'flex items-center gap-2 text-destructive text-sm cursor-help bg-transparent border-0 p-0'}
          data-testid={'browser-translation-warning'}
        >
          <LucideTriangleAlert className={'w-[18px] h-[18px] shrink-0'} aria-hidden="true" />
          <span>{t('Browser-\u00dcbersetzung aktiv')}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent className={'max-w-xs'}>
        {t(
          'Diese Website wird automatisch von Ihrem Browser \u00fcbersetzt. Dies kann zu fehlerhaften \u00dcbersetzungen oder Darstellungsfehlern f\u00fchren. Deaktivieren Sie die Browser-\u00dcbersetzung, um die Inhalte korrekt anzuzeigen.'
        )}
      </TooltipContent>
    </Tooltip>
  ) : null;

  if (isBrowserTranslating) {
    return (
      <div className={'flex items-center'}>
        <div
          className={cn('flex items-center text-destructive uppercase text-sm h-9 px-3', className)}
          data-testid={'language-selector-disabled'}
          translate="no"
        >
          <LucideGlobe className={'w-[18px] h-[18px]'} aria-hidden="true" />
          <span className={'ml-1'}>{lang}</span>
        </div>
        {browserTranslationWarning}
      </div>
    );
  }

  return (
    <>
      <Media lessThan={'desktop'}>
        <Dialog onOpenChange={setIsOpen} open={isOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size={'sm'} className={cn('justify-start pl-0 h-12', className)} translate="no">
              <LucideGlobe className={'w-[24px] h-[24px]'} />
              <div className={'ml-2 text-base'}>{getLanguageNavigationLabel(lang ?? '')}</div>
            </Button>
          </DialogTrigger>
          <DialogContent showFullscreenOnMobile data-testid={'language-dialog'}>
            <DialogHeader>
              <DialogTitle className={'text-left'}>{t('Sprache')}</DialogTitle>
            </DialogHeader>
            <div className={'flex flex-col gap-2'}>
              {['de', 'fr', 'it'].map(l => (
                <TrackNavigationClick key={l} label={getLanguageNavigationLabel(l)}>
                  <Button
                    variant={lang?.toLowerCase() === l ? 'primaryOutline' : 'outline'}
                    onClick={() => handleLanguageChange(l)}
                    className={cn('cursor-pointer', lang?.toLowerCase() === l && 'bg-primary-light')}
                    data-testid={`language-dialog-${l}-button`}
                    translate="no"
                  >
                    {getLanguageNavigationLabel(l)}
                  </Button>
                </TrackNavigationClick>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </Media>
      <Media greaterThanOrEqual={'desktop'}>
        <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size={'sm'}
              className={cn(
                'uppercase text-secondary hover:text-primary mr-0 pr-0',
                isOpen && 'text-primary-dark',
                className
              )}
              data-testid={'language-selector'}
              translate="no"
            >
              <LucideGlobe className={'w-[18px] h-[18px]'} />
              <div className={'ml-1 mr-0.5'}>{lang}</div>
              <LucideChevronDown
                size={14}
                className={cn('transform transition-transform duration-200', isOpen && 'rotate-180')}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={'end'}>
            {['de', 'fr', 'it'].map(l => (
              <TrackNavigationClick key={l} label={getLanguageNavigationLabel(l)}>
                <DropdownMenuItem
                  onClick={() => (lang === l ? undefined : handleLanguageChange(l))}
                  className={cn(
                    'cursor-pointer hover:text-primary focus:bg-transparent focus:text-primary',
                    l === lang && 'text-primary-dark cursor-default font-bold'
                  )}
                  data-testid={`language-selector-${l}`}
                  translate="no"
                >
                  {getLanguageNavigationLabel(l)}
                </DropdownMenuItem>
              </TrackNavigationClick>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Media>
    </>
  );
}
